import CustomerContactSelect from "components/Customer/CustomerContactSelect";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import useApiHook from "Hooks/ApiHook";
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import DuplicateWOAddress, { toAddressForm } from "./DuplicateWOAddress";
import DuplicateWOProducts from "./DuplicateWOProducts";

// Only the shipping address is editable here. Billing is a plain copy-or-not
// checkbox (asked for 2026-10-09): it is the customer's account address and
// is almost never retyped per order, so an editable section for it was weight
// on the dialog. Sending no billing edits leaves the copied billing address
// exactly as it is -- the server skips a column it gets no edits for.
const EDITABLE_ADDRESSES = [
  { option: "Shipping Address", column: "shippingAddress", id: "dup-shipping" },
];

// The W-Info fields the dialog lets you change before the copy is made. Same
// set, same names and same order as the W-Details header strip (WOinfo.jsx) --
// a duplicate is nearly always the same order for a different contact or
// season, and editing it after the fact meant a copy could be sent out under
// the previous contact's name.
const CONTACT_FIELDS = [
  {
    name: "firstName",
    label: "First Name",
    placeholder: "Enter first name",
    className: "col-md-4 col-lg-2",
  },
  {
    name: "lastName",
    label: "Last Name",
    placeholder: "Enter last name",
    className: "col-md-4 col-lg-2",
  },
  {
    name: "department",
    label: "Department",
    placeholder: "Enter department",
    className: "col-md-4 col-lg-2",
  },
  {
    name: "phone",
    label: "Phone",
    placeholder: "Enter phone",
    className: "col-md-4 col-lg-2",
  },
  {
    name: "email",
    label: "Email",
    placeholder: "Enter email",
    className: "col-md-8 col-lg-4",
  },
];

export default function DuplicateWO({
  selectedWorkOrder,
  allProducts = [],
  showModal,
  searchUrl,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    "Shipping Address": true,
    "Billing Address": true,
  });

  // Which products the copy carries (WOProducts.id). Every one starts ticked:
  // an untouched dialog copies everything, as the old "WO Products" box did.
  const [selectedProductIds, setSelectedProductIds] = useState(
    () => new Set(allProducts.map((p) => p.id)),
  );
  useEffect(() => {
    setSelectedProductIds(new Set(allProducts.map((p) => p.id)));
  }, [allProducts]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    department: "",
    phone: "",
    email: "",
    jobName: "",
    subJobName: "",
    shipmentMethod: "",
  });

  // "Job Name" and "Contact" used to be checkboxes here too. Now that the
  // dialog shows those fields, a checkbox saying "do not copy the job name"
  // next to a box holding the job name is the same statement made twice --
  // and the labels collided. Clearing the box IS the way to say it; the flags
  // below are derived from the boxes when the request is built, so the server
  // contract is unchanged.
  //
  // Both addresses keep a copy checkbox, but only shipping has fields. They
  // are PRE-FILLED from the original -- unlike the boxes above. An address is
  // copied by default, so its fields show exactly what is going over; you
  // edit only what differs (agreed 2026-10-09).
  const [addresses, setAddresses] = useState(() => ({
    shippingAddress: toAddressForm(selectedWorkOrder?.shippingAddress),
  }));
  useEffect(() => {
    setAddresses({
      shippingAddress: toAddressForm(selectedWorkOrder?.shippingAddress),
    });
  }, [selectedWorkOrder]);

  const {
    data: shippingMethodsList,
    loading: shippingMethodsLoading,
  } = useApiHook(() =>
    API.get("/shipmentmethods", { headers: authHeader() }),
  );

  /*   All Functions
   ********************************************* */

  // The boxes start EMPTY, not pre-filled from the original -- the user's call
  // (2026-10-09): a duplicate is a new order, so its contact, job name and
  // shipment method are entered fresh (or picked with Select Contact) rather
  // than inherited and forgotten. The server already treats an empty box as
  // "leave it blank on the copy", so what is typed here is exactly what the
  // copy gets.

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Same shape CustomerContactSelect hands every other caller. The fields are
  // nullable on a contact row, so each falls back to "" rather than null --
  // a null would make its input uncontrolled and React would drop the value.
  const handleSelectContact = (contact) => {
    setFormData((prev) => ({
      ...prev,
      firstName: contact?.firstName || "",
      lastName: contact?.lastName || "",
      department: contact?.department || "",
      phone: contact?.phone || "",
      email: contact?.email || "",
    }));
    setShowContactModal(false);
  };

  const handleDuplicateWO = async () => {
    setLoading(true);
    try {
      const { data } = await API.post(
        "/duplicate-WO",
        {
          woId: selectedWorkOrder?.woId,
          // The two flags the dialog no longer shows as checkboxes are
          // derived from the fields, so the server sees the same contract it
          // always did whether or not it understands `overrides`.
          selectedOptions: {
            ...selectedOptions,
            "WO Products": selectedProductIds.size > 0,
            "Job Name": !!String(formData.jobName || "").trim(),
            Contact: CONTACT_FIELDS.some((f) =>
              String(formData[f.name] || "").trim(),
            ),
          },
          // Applied inside the same transaction that creates the copy, so a
          // duplicate never exists with the original's contact on it, not
          // even briefly.
          // Shipping goes as its seven typed fields; the server merges them
          // into the copied address and ignores an address that is not being
          // copied. No billing edits are sent, so the copied billing address
          // is carried over untouched.
          overrides: { ...formData, ...addresses },
          // The ticked products, in the order the list shows them. The server
          // copies only these, and only if they belong to this order.
          productIds: allProducts
            .filter((p) => selectedProductIds.has(p.id))
            .map((p) => p.id),
        },
        { headers: authHeader() },
      );

      if (data?.type === "success") {
        dispatch(
          setNotification({ message: "Added Successfully", type: "success" }),
        );
        setLoading(false);
        showModal(false);
        navigate(
          `/work-order/details/${params?.filterBy}/${data.data?.woId}/${searchUrl}`,
        );
        return data.data;
      }

      if (data?.type === "error") {
        dispatch(setNotification({ message: data?.message, type: "error" }));
        setLoading(false);
        return;
      }

      dispatch(
        setNotification({
          message: "Something went wrong, check console.",
          type: "error",
        }),
      );
      console.log(data);
      setLoading(false);
    } catch (err) {
      console.log(err.message);
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };


  // Every box is pre-filled, so a placeholder is invisible the moment there is
  // a value in it -- "Director" on its own does not say "Department". The
  // W-Details strip gets away with placeholders because it is a familiar fixed
  // row on a page you already know; a dialog does not.
  const field = (id, label, input) => (
    <>
      <label htmlFor={id} className="form-label small text-muted mb-0">
        {label}
      </label>
      {input}
    </>
  );

  // Modal.Header / Modal.Body / Modal.Footer rather than one long div, so the
  // parent's `scrollable` pins the title and the Duplicate button while only
  // the fields scroll. Same arrangement as the Build A Find dialog.
  return (
    <>
      <Modal.Header className="py-2">
        <div>
          <h4 className="mb-0">Duplicate W-{selectedWorkOrder?.woId}</h4>
          {/* The ORIGINAL's job and customer -- the boxes below start empty,
              so this is where you see what you are copying. */}
          <div className="small text-muted">
            {selectedWorkOrder?.jobName || "No job name"}
          </div>
          <div className="small text-muted">
            {selectedWorkOrder?.customer?.customerId
              ? `C-${selectedWorkOrder.customer.customerId} · `
              : ""}
            {selectedWorkOrder?.customerName ||
              selectedWorkOrder?.customer?.customerName ||
              "No customer"}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-3 py-2">
        {(loading || shippingMethodsLoading) && <Spinner />}

        {/*   W-Info for the copy
         ********************************************* */}
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div>
            <p className="fw-bold mb-0">Details for the copy</p>
            <p className="text-muted small mb-2">
              Enter the details for the new order, or use Select Contact — a box
              left empty stays blank on the copy.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-info text-nowrap"
            onClick={() => setShowContactModal(true)}
          >
            Select Contact
          </button>
        </div>

        <div className="row g-2">
          {CONTACT_FIELDS.map((f) => (
            <div className={f.className} key={f.name}>
              {field(
                `dup-${f.name}`,
                f.label,
                <input
                  id={`dup-${f.name}`}
                  type="text"
                  className="form-control form-control-sm"
                  value={formData[f.name]}
                  placeholder={f.placeholder}
                  name={f.name}
                  onChange={handleChange}
                />,
              )}
            </div>
          ))}
        </div>

        <div className="row g-2 mt-0">
          <div className="col-md-6">
            {field(
              "dup-jobName",
              "Job Name",
              <input
                id="dup-jobName"
                type="text"
                className="form-control form-control-sm"
                value={formData.jobName}
                placeholder="Enter Job Name"
                name="jobName"
                onChange={handleChange}
              />,
            )}
          </div>
          <div className="col-md-3">
            {field(
              "dup-subJobName",
              "Sports Name",
              <input
                id="dup-subJobName"
                type="text"
                className="form-control form-control-sm"
                value={formData.subJobName}
                placeholder="Enter Sports Name"
                name="subJobName"
                onChange={handleChange}
              />,
            )}
          </div>
          <div className="col-md-3">
            {field(
              "dup-shipmentMethod",
              "Shipment Method",
              <select
                id="dup-shipmentMethod"
                className="form-select form-select-sm"
                value={formData.shipmentMethod || "null"}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    shipmentMethod:
                      e.target.value === "null"
                        ? ""
                        : parseInt(e.target.value, 10),
                  }))
                }
              >
                <option value={"null"}>None</option>
                {shippingMethodsList?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.shippingMethod}
                  </option>
                ))}
              </select>,
            )}
          </div>
        </div>

        {/*   What to copy
         ********************************************* */}
        <hr className="my-2" />

        {/* Billing: copy it or do not. No fields -- see EDITABLE_ADDRESSES. */}
        <div className="border rounded mb-2 px-2 py-1">
          <div className="form-check mb-0">
            <input
              type="checkbox"
              id="dup-billing"
              className="form-check-input"
              checked={selectedOptions["Billing Address"]}
              onChange={(e) =>
                setSelectedOptions((prev) => ({
                  ...prev,
                  "Billing Address": e.target.checked,
                }))
              }
            />
            <label
              className="form-check-label fw-semibold"
              htmlFor="dup-billing"
            >
              Billing Address
            </label>
          </div>
        </div>

        {/* Shipping: a collapsed bar (copy checkbox + summary) that opens to
            editable fields pre-filled from the original. */}
        {EDITABLE_ADDRESSES.map(({ option, column, id }) => (
          <DuplicateWOAddress
            key={column}
            id={id}
            label={option}
            checked={selectedOptions[option]}
            onToggleCopy={(checked) =>
              setSelectedOptions((prev) => ({ ...prev, [option]: checked }))
            }
            value={addresses[column]}
            onChange={(next) =>
              setAddresses((prev) => ({ ...prev, [column]: next }))
            }
          />
        ))}

        {/*   Which products to copy
         ********************************************* */}
        <hr className="my-2" />
        <DuplicateWOProducts
          products={allProducts}
          selectedIds={selectedProductIds}
          onChange={setSelectedProductIds}
        />
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end gap-1 py-2">
        <button
          onClick={handleDuplicateWO}
          disabled={loading}
          className="btn btn-success"
        >
          Duplicate
        </button>
        <button onClick={() => showModal(false)} className="btn btn-secondary">
          Close
        </button>
      </Modal.Footer>

      {/*   Pick a contact off the customer
       ********************************************* */}
      {/* Nested inside the duplicate dialog, so it needs to sit above it --
          same z-index ladder the approval dialogs use for this component.
          customerId + isPrimaryId match how W-Details opens the same picker,
          because selectedWorkOrder is the same object there. */}
      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        style={{ zIndex: 1000000002 }}
      >
        <Modal.Body>
          <CustomerContactSelect
            handleSelectContact={handleSelectContact}
            customerId={selectedWorkOrder?.customerId}
            isPrimaryId={true}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
